import { useMemo } from 'react';
import { isDependentCpoProperty, isEligibleCpoProperty } from '../../conditionalLogic/utils';
import { getOwnProperty } from '../../utils/getOwnProperty';
import { getActivePropertyWithFilteredOptions } from '../utils/getActivePropertyWithFilteredOptions';
import { useAllPropertyValues } from './useAllPropertyValues';
import { useConditionalPropertyOptionsRulesForObjectType } from './useConditionalPropertyOptionsRulesForObjectType';
import { useProperties } from './useProperties';
export const useConditionalPropertyOptions = ({
  property,
  enableConditionalPropertyOptions = false,
  objectTypeId,
  objectId,
  getAdditionalPropertyValue,
  value
}) => {
  const skipRulesData = !enableConditionalPropertyOptions || !isEligibleCpoProperty({
    property,
    objectTypeId
  });
  const {
    data: conditionalPropertyOptionsRules,
    loading: conditionalPropertyOptionsRulesLoading,
    error: conditionalPropertyOptionsRulesError
  } = useConditionalPropertyOptionsRulesForObjectType(objectTypeId, {
    skip: skipRulesData
  });
  const skipCpoData = useMemo(() => !isDependentCpoProperty(conditionalPropertyOptionsRules || [], property), [conditionalPropertyOptionsRules, property]);
  const {
    data: properties,
    loading: propertiesLoading,
    error: propertiesError
  } = useProperties(objectTypeId, {
    skip: skipCpoData
  });
  const {
    data: allPropertyValues,
    loading: allPropertyValuesLoading,
    error: allPropertyValuesError
  } = useAllPropertyValues({
    objectId,
    objectTypeId,
    skip: skipCpoData
  });
  const conditionalPropertyOptionsRulesForProperty = useMemo(() => {
    return (conditionalPropertyOptionsRules || []).filter(rule => rule.dependentFieldName === property.name);
  }, [conditionalPropertyOptionsRules, property.name]);
  const loading = conditionalPropertyOptionsRulesLoading || propertiesLoading || allPropertyValuesLoading;
  const error = conditionalPropertyOptionsRulesError || propertiesError || allPropertyValuesError;
  return useMemo(() => {
    if (loading) {
      return {
        loading: true,
        error: undefined,
        data: undefined
      };
    }
    if (error) {
      return {
        loading: false,
        error,
        data: undefined
      };
    }
    if (skipCpoData || skipRulesData || properties === undefined) {
      return {
        loading: false,
        error: undefined,
        data: undefined
      };
    }

    // CPO limits to one controlling field per property, so we can safely assume the first
    // field will match all other fields
    const controllingFieldName = conditionalPropertyOptionsRulesForProperty[0].controllingField.fieldName;
    const controllingFieldProperty = getOwnProperty(properties, controllingFieldName);
    const additionalPropertyValue = getAdditionalPropertyValue(controllingFieldName, objectId, objectTypeId);
    const controllingFieldCurrentValue = additionalPropertyValue !== undefined ? additionalPropertyValue : allPropertyValues ? getOwnProperty(allPropertyValues, controllingFieldName) : undefined;
    return {
      loading: false,
      error: undefined,
      data: getActivePropertyWithFilteredOptions({
        conditionalPropertyOptionsRulesForProperty,
        controllingFieldCurrentValue,
        controllingFieldProperty,
        originalProperty: property,
        value
      })
    };
  }, [loading, error, skipCpoData, skipRulesData, properties, conditionalPropertyOptionsRulesForProperty, getAdditionalPropertyValue, objectId, objectTypeId, allPropertyValues, property, value]);
};